import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './AttendancePage.css';
import { getTodayAttendance } from '../services/attendanceApi';
import Icon from './Icon';
import LoadingSpinner from './LoadingSpinner';
import { InlineStatus } from './UIState';
import { useToast } from './ToastProvider';
import { useLocation } from '../hooks/useLocation';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useAttendanceSubmit } from '../hooks/useAttendanceSubmit';
import { readLocalTodayAttendance } from '../utils/todayAttendanceLocal';
import { formatVietnamTimeFromParts } from '../utils/dateTime';

const parseEnvBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null) return defaultValue;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return defaultValue;
  if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) return false;
  return defaultValue;
};

const STRICT_DISABLE_WEB_ATTENDANCE = parseEnvBoolean(
  process.env.REACT_APP_STRICT_WEB_ATTENDANCE_DISABLED,
  false
);

const BUSINESS_REPORT_REQUIRED_DEPARTMENTS = [
  'kinh doanh du an khach hang chinh phu',
  'kinh doanh du an khach hang doanh nghiep 1',
];

const SALES_OFFICE_PROJECT_NAME = 'Văn phòng FSI Tp.HCM';
const SALES_WORK_STATUS_OFFICE = 'Làm việc tại văn phòng';
const SALES_WORK_STATUS_OUTSIDE = 'Công tác bên ngoài văn phòng';

const normalizeVietnameseText = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u0111/g, 'd')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeProjectNameForMatch = (value) =>
  normalizeVietnameseText(value)
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const isSalesOfficeProject = (project) => {
  const target = normalizeProjectNameForMatch(SALES_OFFICE_PROJECT_NAME);
  if (!target) return false;

  const projectCode = normalizeProjectNameForMatch(project?.code || '');
  if (projectCode && projectCode === target) {
    return true;
  }

  const projectName = normalizeProjectNameForMatch(project?.name || '');
  if (!projectName) return false;
  if (projectName === target) {
    return true;
  }

  const hasFsiOffice = projectName.includes('van phong fsi');
  const hasHcm = projectName.includes('hcm') || projectName.includes('ho chi minh');
  return hasFsiOffice && hasHcm;
};

const parseSalesFlag = (value) => {
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'boolean') return value;

  const raw = String(value ?? '').trim();
  if (!raw) return false;

  // Accept numeric-like strings: "1", "1.0", "1,0"
  const numeric = Number(raw.replace(/,/g, '.'));
  if (!Number.isNaN(numeric)) {
    return numeric === 1;
  }

  const normalized = normalizeVietnameseText(raw);
  if (!normalized) return false;
  if (['1', 'true', 'yes', 'y', 'on', 'x', 'co', 'checked', 'check', 'sale', 'sales'].includes(normalized)) {
    return true;
  }

  const numeric2 = Number(normalized.replace(/,/g, '.'));
  if (!Number.isNaN(numeric2)) {
    return numeric2 === 1;
  }
  return false;
};

function AttendancePage({ employee, apiBaseUrl }) {
  const { showToast } = useToast();

  const [todayAttendance, setTodayAttendance] = useState(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [businessReport, setBusinessReport] = useState('');
  const [requiresExplanation, setRequiresExplanation] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [inlineStatus, setInlineStatus] = useState({ type: '', message: '' });

  const [officeWorkReport, setOfficeWorkReport] = useState('');
  const [outsideOfficeReport, setOutsideOfficeReport] = useState('');
  const [provinceTripReport, setProvinceTripReport] = useState('');
  const [trainingEventReport, setTrainingEventReport] = useState('');
  const [salesWorkStatusManual, setSalesWorkStatusManual] = useState('');

  const isSalesEmployee = useMemo(() => parseSalesFlag(employee?.is_sales), [employee?.is_sales]);

  const requiresBusinessReport = useMemo(() => {
    const normalizedDepartment = normalizeVietnameseText(employee?.department || '');
    if (!normalizedDepartment) return false;
    return BUSINESS_REPORT_REQUIRED_DEPARTMENTS.includes(normalizedDepartment);
  }, [employee?.department]);

  const {
    location,
    locationError,
    locationPermission,
    isRequestingLocation,
    projectLocations,
    requestLocation,
    refreshLocation,
  } = useLocation();

  const loadTodayAttendance = useCallback(async () => {
    try {
      const localSnapshot = readLocalTodayAttendance(employee);
      if (localSnapshot) {
        setTodayAttendance(localSnapshot);
      }

      const cachedData = await getTodayAttendance(employee, null, false);
      if (cachedData !== null && cachedData !== undefined) {
        setTodayAttendance(cachedData);
      }

      getTodayAttendance(employee, null, true)
        .then((fresh) => {
          if (fresh !== null && fresh !== undefined) {
            setTodayAttendance(fresh);
          }
        })
        .catch(() => {});
    } catch (error) {
      if (error?.response?.status !== 404) {
        setInlineStatus({ type: 'error', message: 'Không thể tải dữ liệu chấm công hôm nay.' });
      }
      setTodayAttendance(null);
    }
  }, [employee]);

  useEffect(() => {
    loadTodayAttendance();
  }, [loadTodayAttendance]);

  useEffect(() => {
    if (location) {
      setValidationWarnings([]);
      setRequiresExplanation(false);
      setExplanation('');
    }
  }, [location]);

  const { offlineQueueCount, enqueueOfflineIfNeeded, flushOfflineQueue } = useOfflineSync({
    todayAttendance,
    showToast,
    setInlineStatus,
    onSyncSuccess: loadTodayAttendance,
  });

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const checkValidation = (type) => {
    const warnings = [];
    let needExplanation = false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    if (type === 'checkin' && currentTime > 8 * 60 + 30) {
      warnings.push('Giờ vào sau 8h30');
      needExplanation = true;
    }

    if (type === 'checkout' && currentTime < 16 * 60 + 30) {
      warnings.push('Giờ ra trước 16h30');
      needExplanation = true;
    }

    if (type === 'checkin' && location && projectLocations.length > 0) {
      let inRange = false;
      let minDistance = Infinity;
      let nearest = null;

      projectLocations.forEach((project) => {
        if (!project.latitude || !project.longitude || !project.radius) return;
        const distance = calculateDistance(location.latitude, location.longitude, project.latitude, project.longitude);

        if (distance <= project.radius) {
          inRange = true;
          return;
        }

        if (distance < minDistance) {
          minDistance = distance;
          nearest = project;
        }
      });

      if (!inRange) {
        const nearestText = nearest ? `cách ${Math.round(minDistance)}m từ ${nearest.name}` : 'ngoài khu vực làm việc';
        warnings.push(`Ngoài khu vực làm việc (${nearestText})`);
        needExplanation = true;
      }
    }

    setValidationWarnings(warnings);
    setRequiresExplanation(needExplanation);
    return { warnings, needExplanation };
  };

  const canCheckin = !STRICT_DISABLE_WEB_ATTENDANCE && (!todayAttendance || !todayAttendance.checkin_time);
  const canCheckout =
    !STRICT_DISABLE_WEB_ATTENDANCE && Boolean(location && image && (!todayAttendance || !todayAttendance.checkout_time));
  const isCheckoutPhase = Boolean(todayAttendance?.checkin_time && !todayAttendance?.checkout_time);
  const showDetailedCheckoutReport = isCheckoutPhase && isSalesEmployee;
  const showLegacyBusinessReport = requiresBusinessReport && isCheckoutPhase && !isSalesEmployee;
  const showCheckoutReportHint = !showDetailedCheckoutReport && isSalesEmployee;

  const officeProject = useMemo(() => {
    if (!Array.isArray(projectLocations) || projectLocations.length === 0) return null;
    return projectLocations.find((project) => isSalesOfficeProject(project)) || null;
  }, [projectLocations]);

  const salesAutoDetection = useMemo(() => {
    if (!showDetailedCheckoutReport) return null;

    if (!location || !location.latitude || !location.longitude) {
      return {
        status: SALES_WORK_STATUS_OFFICE,
        warning: 'Không lấy được GPS, hệ thống tạm gán "Làm việc tại văn phòng".',
      };
    }

    if (!officeProject || !officeProject.latitude || !officeProject.longitude || !officeProject.radius) {
      return {
        status: SALES_WORK_STATUS_OFFICE,
        warning: 'Chưa tìm thấy cấu hình "Văn phòng FSI Tp.HCM" trong DiaDiemDuAn, hệ thống tạm gán "Làm việc tại văn phòng".',
      };
    }

    const distance = calculateDistance(
      location.latitude,
      location.longitude,
      officeProject.latitude,
      officeProject.longitude
    );

    if (distance <= officeProject.radius) {
      return { status: SALES_WORK_STATUS_OFFICE, warning: '' };
    }

    return { status: SALES_WORK_STATUS_OUTSIDE, warning: '' };
  }, [showDetailedCheckoutReport, location, officeProject]);

  const salesAutoStatus = salesAutoDetection?.status || SALES_WORK_STATUS_OFFICE;
  const salesWorkStatusResolved = salesWorkStatusManual || salesAutoStatus;
  const isSalesWorkStatusOverridden =
    !!salesWorkStatusManual && salesWorkStatusManual !== salesAutoStatus;

  useEffect(() => {
    if (!showDetailedCheckoutReport) {
      setSalesWorkStatusManual('');
    }
  }, [showDetailedCheckoutReport]);

  const resetSalesReportForm = useCallback(() => {
    setSalesWorkStatusManual('');
    setOfficeWorkReport('');
    setOutsideOfficeReport('');
    setProvinceTripReport('');
    setTrainingEventReport('');
  }, []);

  const salesReportData = useMemo(() => {
    if (!showDetailedCheckoutReport) return null;
    return {
      work_status_final: salesWorkStatusResolved,
      office_work_report: officeWorkReport,
      outside_office_report: outsideOfficeReport,
      province_trip_report: provinceTripReport,
      training_event_report: trainingEventReport,
    };
  }, [
    showDetailedCheckoutReport,
    salesWorkStatusResolved,
    officeWorkReport,
    outsideOfficeReport,
    provinceTripReport,
    trainingEventReport,
  ]);

  const hasAnySalesReportContent = useMemo(() => {
    if (!showDetailedCheckoutReport) return false;
    return [officeWorkReport, outsideOfficeReport, provinceTripReport, trainingEventReport].some(
      (value) => String(value || '').trim() !== ''
    );
  }, [showDetailedCheckoutReport, officeWorkReport, outsideOfficeReport, provinceTripReport, trainingEventReport]);

  const { loading, handleCheckin, handleCheckout } = useAttendanceSubmit({
    employee,
    apiBaseUrl,
    location,
    image,
    explanation,
    businessReport,
    requireBusinessReport: requiresBusinessReport,
    isSalesEmployee,
    salesReportData,
    requiresExplanation,
    validationWarnings,
    setValidationWarnings,
    setRequiresExplanation,
    setExplanation,
    setBusinessReport,
    resetSalesReportForm,
    setImage,
    setImagePreview,
    setTodayAttendance,
    loadTodayAttendance,
    checkValidation,
    setInlineStatus,
    showToast,
    enqueueOfflineIfNeeded,
    flushOfflineQueue,
    strictWebAttendanceDisabled: STRICT_DISABLE_WEB_ATTENDANCE,
  });

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { compressImageForAttendance } = await import('../utils/imageCompression');
      const compressed = await compressImageForAttendance(file, null);
      setImage(compressed);

      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(compressed);
    } catch {
      setImage(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const imageSizeMb = image ? image.size / (1024 * 1024) : 0;
  const imageQualityHint = !image
    ? 'Chưa có ảnh.'
    : imageSizeMb <= 1.5
      ? 'Ảnh nhẹ, tốc độ tải tốt.'
      : imageSizeMb <= 3
        ? 'Ảnh ổn, có thể mất thêm thời gian tải.'
        : 'Ảnh lớn, dễ trễ trên mạng yếu.';

  return (
    <div className="attendance-page">
      <div className="attendance-card">
        <InlineStatus type={inlineStatus.type || 'info'} message={inlineStatus.message} />

        {offlineQueueCount > 0 ? (
          <div className="offline-queue-banner">Đang có {offlineQueueCount} bản ghi offline chưa đồng bộ.</div>
        ) : null}

        <h2>Chấm Công Hôm Nay</h2>

        {todayAttendance ? (
          <div className="attendance-info">
            {todayAttendance.checkin_time ? (
              <div className="info-item">
                <span className="label">Giờ vào:</span>
                <span className="value">{formatVietnamTimeFromParts(null, null, todayAttendance.checkin_time)}</span>
              </div>
            ) : null}
            {todayAttendance.checkout_time ? (
              <div className="info-item">
                <span className="label">Giờ ra:</span>
                <span className="value">{formatVietnamTimeFromParts(null, null, todayAttendance.checkout_time)}</span>
              </div>
            ) : null}
            {todayAttendance.work_hours ? (
              <div className="info-item">
                <span className="label">Tổng giờ:</span>
                <span className="value">{todayAttendance.work_hours.toFixed(2)} giờ</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="location-section">
          <h3>
            <Icon name="location" size={20} className="icon-primary" />
            <span>Vị trí hiện tại</span>
          </h3>

          {location ? (
            <div className="location-info">
              <p className="location-address">{location.address || 'Đang cập nhật địa chỉ...'}</p>
              <p className="location-coords">
                {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
              </p>
            </div>
          ) : null}

          {locationError ? (
            <div className="location-error-box">
              <p className="location-error-text">{locationError}</p>
            </div>
          ) : null}

          {locationPermission === 'prompt' || locationPermission === 'denied' || !location ? (
            <button onClick={requestLocation} className="btn-request-location" disabled={isRequestingLocation}>
              {isRequestingLocation ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Đang yêu cầu...</span>
                </>
              ) : (
                <>
                  <Icon name="location" size={18} />
                  <span>Xin quyền vị trí</span>
                </>
              )}
            </button>
          ) : null}

          {location ? (
            <button onClick={refreshLocation} className="btn-refresh-location" disabled={isRequestingLocation}>
              Làm mới vị trí
            </button>
          ) : null}
        </div>

        <div className="image-section">
          <h3>
            <Icon name="camera" size={20} className="icon-primary" />
            <span>Ảnh chấm công</span>
          </h3>
          <div className="image-input-options">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageChange}
              className="image-input"
              id="attendance-image"
            />
            <label htmlFor="attendance-image" className="image-input-label">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="image-preview" />
              ) : (
                <div className="image-placeholder">
                  <span>Chọn ảnh</span>
                </div>
              )}
            </label>
          </div>

          {image ? (
            <button
              onClick={() => {
                setImage(null);
                setImagePreview(null);
              }}
              className="btn-remove-image"
            >
              Xóa ảnh
            </button>
          ) : null}

          <p className="image-quality-hint">{imageQualityHint}</p>
        </div>

        {showLegacyBusinessReport ? (
          <div className="business-report-section">
            <h3>
              <Icon name="info" size={20} className="icon-primary" />
              <span>Báo cáo kinh doanh <span className="required">*</span></span>
            </h3>
            <p className="business-report-hint">Áp dụng cho phòng ban kinh doanh theo cấu hình hệ thống.</p>
            <textarea
              value={businessReport}
              onChange={(e) => setBusinessReport(e.target.value)}
              placeholder="Nhập báo cáo kinh doanh..."
              className="business-report-input"
              rows="4"
            />
          </div>
        ) : null}

        {showCheckoutReportHint ? (
          <div className="sales-report-section">
            <h3>
              <Icon name="info" size={20} className="icon-primary" />
              <span>Báo cáo kinh doanh</span>
            </h3>
            <p className="sales-report-hint">Biểu mẫu này mở khi bạn đã chấm công vào và thực hiện chấm công ra.</p>
          </div>
        ) : null}

        {showDetailedCheckoutReport ? (
          <div className="sales-report-section">
            <h3>
              <Icon name="info" size={20} className="icon-primary" />
              <span>
                Báo cáo kinh doanh <span className="required">*</span>
              </span>
            </h3>
            <p className="sales-report-hint">
              Mục 1 tự động theo GPS (dựa trên dự án Văn phòng FSI Tp.HCM) và bạn có thể sửa tay nếu detect sai. Mục 2-5 cần có ít nhất 1 mục có nội dung.
            </p>

            <div className="sales-status-block">
              <label className="sales-field-label" htmlFor="sales-work-status-final">
                1. Tình trạng làm việc (tự động + cho phép sửa tay)
              </label>
              <select
                id="sales-work-status-final"
                className="sales-report-input"
                value={salesWorkStatusResolved}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  if (nextValue === salesAutoStatus) {
                    setSalesWorkStatusManual('');
                  } else {
                    setSalesWorkStatusManual(nextValue);
                  }
                }}
              >
                <option value={SALES_WORK_STATUS_OFFICE}>{SALES_WORK_STATUS_OFFICE}</option>
                <option value={SALES_WORK_STATUS_OUTSIDE}>{SALES_WORK_STATUS_OUTSIDE}</option>
              </select>
              <p className="sales-status-warning">
                Tự động: {salesAutoStatus}
                {isSalesWorkStatusOverridden ? ' | Đã sửa tay' : ''}
              </p>
              {salesAutoDetection?.warning ? (
                <p className="sales-status-warning">{salesAutoDetection.warning}</p>
              ) : null}
            </div>

            <div className="sales-report-fields">
              <label className="sales-field-label" htmlFor="office-work-report">
                2. Làm việc tại văn phòng
              </label>
              <textarea
                id="office-work-report"
                value={officeWorkReport}
                onChange={(e) => setOfficeWorkReport(e.target.value)}
                placeholder="Nhập nội dung công việc tại văn phòng..."
                className="sales-report-input"
                rows="3"
              />

              <label className="sales-field-label" htmlFor="outside-office-report">
                3. Công tác bên ngoài văn phòng
              </label>
              <textarea
                id="outside-office-report"
                value={outsideOfficeReport}
                onChange={(e) => setOutsideOfficeReport(e.target.value)}
                placeholder="Nhập nội dung công tác bên ngoài văn phòng..."
                className="sales-report-input"
                rows="3"
              />

              <label className="sales-field-label" htmlFor="province-trip-report">
                4. Công tác tỉnh
              </label>
              <textarea
                id="province-trip-report"
                value={provinceTripReport}
                onChange={(e) => setProvinceTripReport(e.target.value)}
                placeholder="Nhập nội dung công tác tỉnh..."
                className="sales-report-input"
                rows="3"
              />

              <label className="sales-field-label" htmlFor="training-event-report">
                5. Đào tạo / hội thảo bên ngoài văn phòng
              </label>
              <textarea
                id="training-event-report"
                value={trainingEventReport}
                onChange={(e) => setTrainingEventReport(e.target.value)}
                placeholder="Nhập nội dung đào tạo / hội thảo..."
                className="sales-report-input"
                rows="3"
              />
            </div>

            {!hasAnySalesReportContent ? (
              <p className="explanation-hint">Vui lòng nhập ít nhất 1 nội dung ở mục 2, 3, 4 hoặc 5.</p>
            ) : null}
          </div>
        ) : null}

        {requiresExplanation ? (
          <>
            {validationWarnings.length > 0 ? (
              <div className="explanation-warning" role="alert">
                <h3>
                  <Icon name="warning" size={20} className="icon-warning" />
                  <span>Cần giải trình</span>
                </h3>
                <ul className="warning-list">
                  {validationWarnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="explanation-section">
              <h3>
                <Icon name="info" size={20} className="icon-info" />
                <span>
                  Giải trình <span className="required">*</span>
                </span>
              </h3>
              <textarea
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                placeholder="Nhập giải trình..."
                className="explanation-input"
                rows="3"
                required
              />
            </div>
          </>
        ) : null}

        {STRICT_DISABLE_WEB_ATTENDANCE ? (
          <div className="explanation-warning" role="alert">
            <h3>
              <Icon name="warning" size={20} className="icon-warning" />
              <span>Chế độ strict đang bật</span>
            </h3>
            <ul className="warning-list">
              <li>Web thường không được phép chấm công.</li>
              <li>Vui lòng dùng App Web Mobile để chấm công.</li>
            </ul>
          </div>
        ) : (
          <div className="action-buttons">
            <button
              onClick={handleCheckin}
              disabled={loading || !canCheckin}
              className={`btn-checkin ${!canCheckin ? 'disabled' : ''}`}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Đang xử lý...</span>
                </>
              ) : (
                <>
                  <Icon name="checkCircle" size={20} />
                  <span>Chấm Công Vào</span>
                </>
              )}
            </button>
            <button
              onClick={handleCheckout}
              disabled={loading || !canCheckout}
              className={`btn-checkout ${!canCheckout ? 'disabled' : ''}`}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Đang xử lý...</span>
                </>
              ) : (
                <>
                  <Icon name="logout" size={20} />
                  <span>Chấm Công Ra</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AttendancePage;


